/**
 * offlineSync — replays the IndexedDB outbox (offlineQueue.ts) through the normal
 * API layer when connectivity returns. See offlineQueue.ts for why we re-dispatch
 * the operation INTENT rather than a stored raw request.
 *
 * Conflict model (per CLAUDE.md §76): last-write-wins. Each queued op is an
 * append (a new harvest / care event), so replaying in createdAt order and
 * letting the server stamp its own timestamp is safe — there is no destructive
 * update to reconcile.
 */
import { useEffect, useState } from 'react'
import type { AxiosError } from 'axios'
import { harvestsApi, activityApi, plantsApi, type HarvestEvent, type ParsedEvent, type Observation } from './api'
import {
  enqueue, allOps, updateOp, removeOp, subscribe, pendingCount,
  type QueuedKind, type QueuedOp,
} from './offlineQueue'

// ── Replay dispatch: reconstruct the API call from (kind, payload) ──────────────
async function replay(kind: QueuedKind, payload: unknown): Promise<void> {
  switch (kind) {
    case 'harvest':
      await harvestsApi.create(payload as Partial<HarvestEvent>)
      return
    case 'activity':
      await activityApi.commit(payload as ParsedEvent[])
      return
    case 'observation': {
      // addObservation takes (plantId, data) — both are stored in the payload.
      const { plant_id, data } = payload as { plant_id: string; data: Partial<Observation> }
      await plantsApi.addObservation(plant_id, data)
      return
    }
    default:
      throw new Error(`unknown queued op kind: ${kind}`)
  }
}

// A failure with no HTTP response = we never reached the server (offline / DNS /
// timeout). Those are retryable. A response (4xx/5xx) is a real server rejection —
// the payload is bad, so don't silently queue it; let the caller surface the error.
function isConnectivityError(e: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true
  const ax = e as AxiosError
  return !!ax && ax.isAxiosError === true && !ax.response
}

/**
 * submitOrQueue — the single entry point the UI uses for an offline-capable
 * mutation. Online: send immediately; if the send fails for connectivity reasons,
 * fall back to the queue. Offline: queue straight away. Returns whether it was
 * queued so the caller can show "saved" vs "saved offline — will sync".
 */
export async function submitOrQueue(
  kind: QueuedKind,
  payload: unknown,
  label: string,
): Promise<{ queued: boolean }> {
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    try {
      await replay(kind, payload)
      return { queued: false }
    } catch (e) {
      if (!isConnectivityError(e)) throw e // genuine server rejection — surface it
      // otherwise fall through and queue
    }
  }
  await enqueue({ kind, payload, label })
  return { queued: true }
}

// ── Drain ───────────────────────────────────────────────────────────────────────
let draining = false

/** Replay every outstanding op in order. Single-flight; safe to call freely. */
export async function drainQueue(): Promise<void> {
  if (draining) return
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  draining = true
  try {
    const ops = await allOps()
    for (const op of ops) {
      await updateOp(op.id, { status: 'syncing' })
      try {
        await replay(op.kind, op.payload)
        await removeOp(op.id)
      } catch (e) {
        if (isConnectivityError(e)) {
          // Still offline / lost mid-drain — revert and stop; we'll retry on the
          // next 'online' event. Leave remaining ops untouched.
          await updateOp(op.id, { status: 'pending' })
          break
        }
        // Server rejected this op (4xx/5xx). Mark failed but keep going so one
        // bad op doesn't block the rest.
        await updateOp(op.id, {
          status: 'failed',
          attempts: (op.attempts ?? 0) + 1,
          error: errMessage(e),
        })
      }
    }
  } finally {
    draining = false
  }
}

/** Reset failed ops to pending and try again (manual retry from the sync panel). */
export async function retryFailed(): Promise<void> {
  const ops = await allOps()
  await Promise.all(ops.filter((o) => o.status === 'failed').map((o) => updateOp(o.id, { status: 'pending', error: undefined })))
  await drainQueue()
}

/** Drop failed ops the user has given up on. */
export async function clearFailed(): Promise<void> {
  const ops = await allOps()
  await Promise.all(ops.filter((o) => o.status === 'failed').map((o) => removeOp(o.id)))
}

function errMessage(e: unknown): string {
  const ax = e as AxiosError<{ error?: string; message?: string }>
  return ax?.response?.data?.error || ax?.response?.data?.message || (e as Error)?.message || 'Sync failed'
}

// ── App wiring ───────────────────────────────────────────────────────────────────
let inited = false
export function initOfflineSync(): void {
  if (inited || typeof window === 'undefined') return
  inited = true
  window.addEventListener('online', () => { void drainQueue() })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) void drainQueue()
  })
  // Drain anything left over from a previous session (closed before reconnecting).
  void drainQueue()
}

// ── React hooks ───────────────────────────────────────────────────────────────────
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine)
  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])
  return online
}

export function useOutbox(): { ops: QueuedOp[]; count: number } {
  const [ops, setOps] = useState<QueuedOp[]>([])
  useEffect(() => {
    let alive = true
    const refresh = () => { void allOps().then((o) => { if (alive) setOps(o) }) }
    refresh()
    const unsub = subscribe(refresh)
    return () => { alive = false; unsub() }
  }, [])
  return { ops, count: ops.length }
}

export { pendingCount }
